import React, { Component } from "react";

import Events from "core-web/libs/Events";
import PropTypes from "prop-types";
import SearchContainer from "core-web/containers/SearchContainer";
import { SearchEvents } from "core-web/libs/Events/constants";
import getQueryParams from "core-web/utils/getQueryParams";
import { inServer } from "core-web/constants";
import { injectModel } from "core-web/state";
import setAllProducts from "core-web/state/models/Products/dispatches/setAllProducts";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";

class SearchFilterContainer extends Component {
    static propTypes = {
        history: PropTypes.object.isRequired,
        indices: PropTypes.object.isRequired,
        location: PropTypes.object.isRequired,
        productProps: PropTypes.shape({
            config: PropTypes.shape(
                PropTypes.shape({
                    type: PropTypes.string,
                    param: PropTypes.string, // url param label
                    label: PropTypes.string,
                }),
            ),
            distinct: PropTypes.bool,
            filters: PropTypes.array,
            firstPageSize: PropTypes.number,
            historyPush: PropTypes.bool,
            pageSize: PropTypes.number,
            ruleContexts: PropTypes.array,
            saveSortState: PropTypes.bool,
            responseCallback: PropTypes.func,
            sort: PropTypes.string,
            sorts: PropTypes.shape({
                key: PropTypes.string,
            }),
        }),
        render: PropTypes.func.isRequired,
        renderProps: PropTypes.object,
        search: PropTypes.string,
        t: PropTypes.func,
        view: PropTypes.string,
    };

    static defaultProps = {
        productProps: {
            distinct: true,
            filters: [],
            firstPageSize: null,
            historyPush: false,
            pageSize: 24,
            responseCallback: null,
            ruleContexts: [],
            saveSortState: false,
            sort: "products",
            sorts: null,
        },
        renderProps: {},
        search: "",
        t: null,
        view: "products",
    };

    constructor(props) {
        super(props);

        this.state = {
            sort: (props.productProps.saveSortState && this.url.getSort()) || props.productProps.sort,
            filters: [],
        };

        // these wont be camelcase, because of index names in algolia
        this.sorts = props.productProps.sorts || {
            products: props.t("popular", { context: "filter" }), // default
            products_price_asc: props.t("ascending_price"),
            products_price_desc: props.t("descending_price"),
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        const {
            productProps: { config, filters, firstPageSize, pageSize, ruleContexts },
            renderProps,
            search,
            location,
            view,
        } = this.props;

        if (
            JSON.stringify(filters) !== JSON.stringify(nextProps.productProps.filters) ||
            JSON.stringify(config) !== JSON.stringify(nextProps.productProps.config) ||
            JSON.stringify(ruleContexts) !== JSON.stringify(nextProps.productProps.ruleContexts) ||
            search !== nextProps.search ||
            JSON.stringify(renderProps) !== JSON.stringify(nextProps.renderProps) ||
            (location.search !== nextProps.location.search &&
                location.pathname === nextProps.location.pathname &&
                view === nextProps.view)
        ) {
            this.setState({ filters: [] });

            if (search !== nextProps.search && location.pathname === nextProps.location.pathname) {
                this.url.set();
            }

            if (this.state.filters.length === 0) {
                return true;
            }
        }

        if (
            JSON.stringify(pageSize) !== JSON.stringify(nextProps.productProps.pageSize) ||
            JSON.stringify(firstPageSize) !== JSON.stringify(nextProps.productProps.firstPageSize) ||
            view !== nextProps.view
        ) {
            return true;
        }

        if (JSON.stringify(this.state) !== JSON.stringify(nextState)) {
            return true;
        }

        if (
            location.search !== nextProps.location.search &&
            location.pathname === nextProps.location.pathname &&
            view === nextProps.view
        ) {
            return true;
        }

        return false;
    }

    // Handle different types of clearing of filter caused by user interactions
    // eslint-disable-next-line react/sort-comp
    clear = {
        byValue: (key, value) => {
            const { filters } = this.state;
            const { config } = this.props.productProps;
            let newState = JSON.parse(JSON.stringify(filters));

            newState = newState.map((f) => {
                if (f.key !== key) {
                    return f;
                }

                const type = config[key]?.type;
                switch (type) {
                    case "tree":
                    case "checkbox":
                        f.selected = f.selected.filter((s) => s !== value);
                        break;
                    case "range":
                        f.selected = f.selected.filter((s) => !value.includes(s));
                        break;
                    case "radio":
                        f.selected = "";
                        break;
                    default:
                }
                return f;
            });

            this.url.set(newState);
            this.setState({ filters: newState });
        },

        byKey: (key) => {
            const { filters } = this.state;
            let newState = JSON.parse(JSON.stringify(filters));

            newState = newState.map((f) => {
                if (f.key === key) {
                    f.selected = [];
                }
                return f;
            });

            this.url.set(newState);
            this.setState({ filters: newState, allProducts: [] });
        },

        filter: () => {
            const { filters } = this.state;
            const { config } = this.props.productProps;
            let newState = JSON.parse(JSON.stringify(filters));

            newState = newState.reduce((a, filter) => {
                const type = config[filter.key]?.type;
                switch (type) {
                    case "tree":
                    case "checkbox":
                    case "range":
                        return [...a, { ...filter, selected: [] }];
                    case "radio":
                        return [...a, { ...filter, selected: "" }];
                    default:
                        return [...a];
                }
            }, []);

            this.url.set(newState);
            this.setState({ filters: newState, allProducts: [] });
        },

        all: () => {
            const { filters } = this.state;
            const { config } = this.props.productProps;
            let newState = JSON.parse(JSON.stringify(filters));

            newState = newState.reduce((a, filter) => {
                const type = config[filter.key]?.type;
                switch (type) {
                    case "tree":
                    case "checkbox":
                    case "range":
                        return [...a, { ...filter, selected: [] }];
                    case "radio":
                        return [...a, { ...filter, selected: "" }];
                    default:
                        return [...a];
                }
            }, []);

            if (this.props.productProps.saveSortState) {
                window.sessionStorage.setItem("appliedSort", this.props.productProps.sort);
            }

            this.url.set(newState, this.props.productProps.sort);
            this.setState({ filters: newState, allProducts: [], sort: this.props.productProps.sort });
        },
    };

    get = {
        valid: () => {
            const { config } = this.props.productProps;
            return Object.entries(config).map(([k]) => k);
        },

        labels: () => {
            const { config } = this.props.productProps;
            const labels = {};
            Object.entries(config).forEach(([k, a]) => {
                labels[k] = a.label;
            });
            return labels;
        },

        types: () => {
            const { config } = this.props.productProps;
            const types = {};
            Object.entries(config).forEach(([k, a]) => {
                types[k] = a.type;
            });
            return types;
        },

        params: () => {
            const { config } = this.props.productProps;
            const params = {};
            Object.entries(config).forEach(([k, a]) => {
                params[k] = a.param;
            });
            return params;
        },
    };

    // Handle user interactions with filter and sort
    handleChange = {
        filter: {
            // Filter by value (checkbox)
            value: (key, value) => {
                const { filters } = this.state;
                const { config } = this.props.productProps;
                const newState = JSON.parse(JSON.stringify(filters));
                const index = newState.findIndex((v) => v.key === key);

                // check if key exists
                if (index === -1) {
                    return;
                }

                let isSelected = null;
                const type = config[key]?.type;

                switch (type) {
                    case "tree":
                    case "checkbox":
                        isSelected = newState[index].selected.indexOf(value);
                        if (isSelected === -1) {
                            newState[index].selected.push(value);
                        } else {
                            newState[index].selected.splice(isSelected, 1);
                        }
                        break;
                    case "radio":
                        isSelected = newState[index].selected === value;
                        if (!isSelected) {
                            newState[index].selected = value;
                        } else {
                            newState[index].selected = "";
                        }
                        break;
                    default:
                }

                this.url.set(newState);
                this.setState({ filters: newState });
            },

            // Filter by range (slider)
            range: (key, min, max) => {
                const { filters } = this.state;
                const newState = JSON.parse(JSON.stringify(filters));
                const index = newState.findIndex((v) => v.key === key);

                // check if key exists
                if (index === -1) {
                    return;
                }

                newState[index].selected = [min, max];

                this.url.set(newState);
                this.setState({ filters: newState });
            },
        },

        // Sort products by changing index
        sort: (index) => {
            const { saveSortState } = this.props.productProps;
            if (!inServer && saveSortState) {
                window.sessionStorage.setItem("appliedSort", index);
                this.url.set(this.state.filters, index);
            }
            this.setState({ sort: index });
        },
    };

    url = {
        set: (filters = this.state.filters, sort = this.state.sort) => {
            const {
                productProps: { config, historyPush },
                history,
                location,
            } = this.props;

            // OBS we have to use window.location instead of withRouter location. Because of withRouter is to slow // Robin
            const params = getQueryParams(window.location);

            delete params.page;

            filters.forEach((filter) => {
                const type = config[filter.key]?.type;
                switch (type) {
                    case "tree":
                    case "checkbox":
                    case "range":
                        if (filter.selected.length > 0) {
                            params[config[filter.key].param] = filter.selected
                                .map((value) => encodeURIComponent(value))
                                .join(",");
                        } else {
                            delete params[config[filter.key].param];
                        }
                        break;
                    case "radio":
                        if (filter.selected.length > 0) {
                            params[config[filter.key].param] = encodeURIComponent(filter.selected);
                        } else {
                            delete params[config[filter.key].param];
                        }
                        break;
                    default:
                }
            });

            if (this.props.productProps.saveSortState) {
                const sortParam = config["sort"]?.param;
                if (sort) {
                    params[sortParam] = sort;
                } else {
                    delete params[sortParam];
                }
            }

            if (historyPush) {
                const url = `?${Object.entries(params)
                    .map((q) => q.join("="))
                    .join("&")}`;

                history.push(`${location.pathname}${url}`);
            } else {
                history.replace(
                    `${location.pathname}?${Object.entries(params)
                        .map((q) => q.join("="))
                        .join("&")}`,
                );
            }
        },

        get: () => {
            const { location } = this.props;
            const queries = getQueryParams(location);
            const params = [];

            Object.keys(queries).forEach((p) => {
                if (p === "sort") {
                    return;
                }
                const entity = Object.entries(this.get.params()).find((k) => k[1] === p);
                if (entity) {
                    params[entity[0]] = queries[p].split(",").map((q) => decodeURIComponent(q));
                }
            });

            return params;
        },

        getSort: () => {
            const {
                location,
                productProps: { saveSortState },
            } = this.props;
            const queries = getQueryParams(location);

            if (queries["sort"]) {
                return queries["sort"];
            } else if (!inServer && saveSortState) {
                return window.sessionStorage.getItem("appliedSort");
            }
        },

        apply: () => {},
    };

    // Build filter structure with Algolia facets
    format = {
        prototype: {
            filters: (filters) => {
                return filters.reduce((a, filter) => {
                    switch (filter.type) {
                        case "tree":
                            return [
                                ...a,
                                this.format.prototype.tree(
                                    filter.key,
                                    filter.label,
                                    filter.stats,
                                    filter.values,
                                    filter.selected,
                                    filter.uom,
                                ),
                            ];
                        case "checkbox":
                            return [
                                ...a,
                                this.format.prototype.checkbox(
                                    filter.key,
                                    filter.label,
                                    filter.stats,
                                    filter.values,
                                    filter.selected,
                                    filter.uom,
                                ),
                            ];
                        case "range":
                            return [
                                ...a,
                                this.format.prototype.range(
                                    filter.key,
                                    filter.label,
                                    filter.stats,
                                    filter.values,
                                    filter.selected,
                                    filter.uom,
                                ),
                            ];
                        case "radio":
                            return [
                                ...a,
                                this.format.prototype.radio(
                                    filter.key,
                                    filter.label,
                                    filter.stats,
                                    filter.values,
                                    filter.selected,
                                    filter.uom,
                                ),
                            ];
                        default:
                            return a;
                    }
                }, []);
            },

            tree: (key, label, stats, values, selected, uom) => ({
                key, // string
                label, // string
                stats, // object
                selected, // all selected values
                values: values.map((v) => ({
                    label: v.label,
                    value: v.value,
                    selected: selected.includes(v.value.toString()),
                    hits: v.hits,
                })), // [{label: string, value: string, selected: bool}]
                totalSelected: selected ? selected.length : 0, // int
                uom,
            }),

            checkbox: (key, label, stats, values, selected, uom) => ({
                key, // string
                label, // string
                stats, // object
                selected, // all selected values
                values: values.map((v) => ({
                    label: v.label,
                    value: v.value,
                    selected: selected.includes(v.value.toString()),
                    hits: v.hits,
                })), // [{label: string, value: string, selected: bool}]
                totalSelected: selected ? selected.length : 0, // int
                uom,
            }),

            range: (key, label, stats, values, selected, uom) => ({
                key, // string
                label, // string
                stats, // object
                values, // [min, max]
                selected, // [min, max]
                // totalSelected: selected ? selected.length : 0, // int
                uom,
            }),

            radio: (key, label, stats, values, selected, uom) => ({
                key, // string
                label, // string
                stats, // object
                values: values.map((v) => ({
                    label: v.label,
                    value: v.value,
                    hits: v.hits,
                    selected: v.value.toString() === selected,
                })), // [{label: string, value: string, selected: bool, totalHits: number}]
                selected: selected.length > 0, // string
                uom,
            }),
        },

        filters: (filters = this.state.filters) => this.format.prototype.filters(filters),

        selectedFilters: (filters = this.state.filters) => {
            const { config } = this.props.productProps;
            const selected = [];
            this.format.prototype.filters(filters).forEach((f) => {
                const type = config[f.key]?.type;
                switch (type) {
                    case "tree":
                        f.values.forEach((v) => {
                            if (v.selected) {
                                selected.push({
                                    key: f.key,
                                    label: v.label,
                                    value: v.value,
                                    hits: v.hits,
                                });
                            }
                        });
                        break;
                    case "checkbox":
                        f.values.forEach((v) => {
                            if (v.selected) {
                                selected.push({
                                    key: f.key,
                                    label: v.label,
                                    value: v.value,
                                    hits: v.hits,
                                });
                            }
                        });
                        break;
                    case "range":
                        if (f.selected.length === 2) {
                            selected.push({
                                key: f.key,
                                label: `${f.selected[0]} - ${f.selected[1]}`,
                                value: f.selected,
                            });
                        }
                        break;
                    case "radio":
                        f.values.forEach((v) => {
                            if (v.selected) {
                                selected.push({
                                    key: f.key,
                                    label: v.label,
                                    value: v.value,
                                    hits: v.hits,
                                });
                            }
                        });
                        break;
                    default:
                }
            });
            return selected;
        },

        sorts: () => {
            const sort = this.state.sort || this.props.productProps.sort;

            return Object.keys(this.sorts).map((key) => ({
                index: key,
                label: this.sorts[key],
                selected: sort === key,
            }));
        },
    };

    filters = {
        prototype: {
            filters: (filters, filterStats) => {
                const { config } = this.props.productProps;
                const urlParams = this.url.get();
                const order = Object.keys(config);
                return Object.keys(filters)
                    .reduce((a, k) => {
                        if (this.get.valid().indexOf(k) === -1 && filters[k]) {
                            return a;
                        }

                        let uom;
                        if (k.indexOf("parametrics") !== -1) {
                            const code = k.split(".")[1];

                            uom = `parametrics.${code}.uom`;
                            uom = filters[uom];

                            uom = uom ? Object.keys(uom)[0] : null;
                        }

                        switch (this.get.types()[k]) {
                            case "tree":
                                return [
                                    ...a,
                                    this.filters.prototype.checkbox(
                                        k,
                                        filterStats[k],
                                        urlParams[k] ? urlParams[k] : [],
                                        filters[k],
                                        uom,
                                    ),
                                ];
                            case "checkbox":
                                return [
                                    ...a,
                                    this.filters.prototype.checkbox(
                                        k,
                                        filterStats[k],
                                        urlParams[k] ? urlParams[k] : [],
                                        filters[k],
                                        uom,
                                    ),
                                ];
                            case "range":
                                return [
                                    ...a,
                                    this.filters.prototype.range(
                                        k,
                                        filterStats[k],
                                        urlParams[k] ? urlParams[k] : [],
                                        uom,
                                    ),
                                ];
                            case "radio":
                                return [
                                    ...a,
                                    this.filters.prototype.radio(
                                        k,
                                        filterStats[k],
                                        urlParams[k] ? urlParams[k][0] : "",
                                        filters[k],
                                        uom,
                                    ),
                                ];
                            default:
                                return a;
                        }
                    }, [])
                    .filter((i) => i)
                    .sort((a, b) => {
                        if (order.indexOf(a.key) > order.indexOf(b.key)) {
                            return 1;
                        }
                        if (order.indexOf(b.key) > order.indexOf(a.key)) {
                            return -1;
                        }
                        return 0;
                    });
            },

            tree: (key, stats, selected, values, uom) => {
                const { config } = this.props.productProps;
                return {
                    key, // string
                    type: config[key].type, // string
                    label: config[key].label, // string
                    stats: stats ? stats : values, // object
                    selected, // [string, string, ...]
                    values: Object.keys(values).map((v) => ({
                        label: v,
                        value: v,
                        prefix: "",
                        hits: values[v],
                    })), // [{label: string, value: string, prefix: string}]
                    uom,
                };
            },

            checkbox: (key, stats, selected, values, uom) => {
                const { config } = this.props.productProps;
                return {
                    key, // string
                    type: config[key].type, // string
                    label: config[key].label, // string
                    stats: stats ? stats : values, // object
                    selected, // [string, string, ...]
                    values: Object.keys(values).map((v) => ({
                        label: v,
                        value: v,
                        prefix: "",
                        hits: values[v],
                    })), // [{label: string, value: string, prefix: string}]
                    uom,
                };
            },

            range: (key, stats, selected, uom) => {
                const { config } = this.props.productProps;
                if (!stats) {
                    return null;
                }

                return {
                    key, // string
                    type: config[key].type, // string
                    label: config[key].label, // string
                    stats, // object
                    selected, // [min, max]
                    values: [stats.min, stats.max], // [min, max]
                    uom,
                };
            },

            radio: (key, stats, selected, values, uom) => {
                const { config } = this.props.productProps;
                return {
                    key, // string
                    type: config[key].type, // string
                    label: config[key].label, // string
                    stats: stats ? stats : values, // object
                    selected, // string
                    values: Object.keys(values).map((v) => ({
                        label: v,
                        value: v,
                        hits: values && values[v],
                        prefix: "",
                    })), // [{label: string, value: string, prefix: string, totalHits: number}]
                    uom,
                };
            },
        },

        default: () => {
            return this.props.productProps.filters || [];
        },

        selected: () => {
            const { filters } = this.state;
            const selected = {
                tree: (selected, key, prefix) =>
                    selected.map((v) => {
                        return this.filters.combine.tree(key, v, prefix);
                    }),
                checkbox: (selected, key, prefix) =>
                    selected.map((v) => {
                        return this.filters.combine.checkbox(key, v, prefix);
                    }),
                range: (selected, key, prefix) => {
                    if (isNaN(selected[0]) || isNaN(selected[1])) {
                        return null;
                    }

                    return [this.filters.combine.range(key, selected[0], selected[1], prefix)];
                },
                radio: (selected, key, prefix) => {
                    return [this.filters.combine.radio(key, selected, prefix)];
                },
            };

            const facetsForFilters = [];

            const selectedFilters = filters
                .map((f) => {
                    if (f.selected?.length) {
                        facetsForFilters.push(f.key);
                    }

                    const type = this.get.types()[f.key];

                    return f.selected.length > 0 ? selected[type] && selected[type](f.selected, f.key, f.prefix) : [];
                })
                .filter((i) => i && i.length !== 0);

            // selected filters from url
            if (filters.length === 0) {
                const query = this.url.get();
                Object.entries(query).forEach(([key, array]) => {
                    facetsForFilters.push(key);
                    const type = this.get.types()[key];
                    const filter = selected[type](array, key);

                    filter && selectedFilters.push(filter);
                });
            }

            const dynamicFilters = [];

            facetsForFilters.forEach((current) => {
                const filterWithoutCurrent = selectedFilters.filter(
                    (filter) => JSON.stringify(filter).indexOf(current) === -1,
                );

                dynamicFilters.push({ facets: [current], filters: filterWithoutCurrent });
            });

            return { selectedFilters, dynamicFilters };
        },

        combine: {
            tree: (key, value, prefix = null) => {
                return `${(prefix ? `${prefix} ` : "") + key}:"${
                    typeof value === "string" ? value.replace('"', '\\"') : value
                }"`;
            },
            checkbox: (key, value, prefix = null) => {
                return `${(prefix ? `${prefix} ` : "") + key}:"${
                    typeof value === "string" ? value.replace('"', '\\"') : value
                }"`;
            },
            range: (key, min, max, prefix = null) => {
                return `${(prefix ? `${prefix} ` : "") + key}:${min} TO ${max}`;
            },
            radio: (key, value, prefix = null) => {
                return `${(prefix ? `${prefix} ` : "") + key}: "${value}"`;
            },
        },
    };

    init = (response) => {
        let filters = this.filters.prototype.filters(response.filters, response.filter_stats);
        filters = this.initFiltersModifier(filters);

        if (!inServer && this.props.productProps.saveSortState) {
            const sessionSort = window.sessionStorage.getItem("appliedSort");
            if (sessionSort) {
                this.url.set(this.state.filters, sessionSort);
                this.setState({ sort: sessionSort });
            }
        }

        return filters;
    };

    initFiltersModifier = (filters) => {
        filters.forEach((filter, index) => {
            if (filter?.values?.some((value) => value?.label === "false" || value?.label === "true")) {
                const { t } = this.props;

                const values = filter.values.map((value) => {
                    const label = value?.label?.replace("false", t("no")).replace("true", t("yes"));

                    return {
                        ...value,
                        label,
                    };
                });

                filters[index].values = values;
            }
        });

        return filters;
    };

    isAnySelected = (filters = this.state.filters) => filters.reduce((a, b) => a + b.selected?.length, 0) > 0;

    // Format response data
    responseCallback = (response) => {
        if (!response) {
            return null;
        }

        const { filters } = this.state;

        let newFilters = JSON.parse(JSON.stringify(filters));

        newFilters = this.init(response);

        setAllProducts(response.products);

        const data = {
            handleChange: this.handleChange,
            clear: this.clear,
            isAnySelected: this.isAnySelected(newFilters),
            // hasFilter: this.hasFilter,
            pagination: response.pagination,
            products: response.products,
            productsCount: response.productsCount,
            page: response.page,
            offset: response.offset,
            pageSize: response.pageSize,
            pageCount: response.pageCount,
            hitsCount: response.hitsCount,
            hasMore: response.hasMore,
            filters: this.format.filters(newFilters),
            selectedFilters: this.format.selectedFilters(newFilters),
            sorts: this.format.sorts(),
            queryId: response.queryId,
        };

        this.setState({ filters: newFilters });
        //To update and trigger the new data inc for the filter overlay
        Events.trigger(SearchEvents.MOBILE_FILTER, data);

        return data;
    };

    render() {
        const { sort } = this.state;
        const {
            indices,
            productProps: { distinct, pageSize, ruleContexts, firstPageSize },
            render,
            renderProps,
            search,
            view,
        } = this.props;

        const { selectedFilters, dynamicFilters } = this.filters.selected();

        return (
            <SearchContainer
                indices={indices}
                view={view}
                productProps={{
                    distinct,
                    pageSize,
                    ruleContexts,
                    filters: {
                        default: this.filters.default(),
                        selected: selectedFilters,
                        dynamic: dynamicFilters,
                    },
                    index: sort,
                    firstPageSize,
                    responseCallback: this.responseCallback,
                }}
                renderProps={renderProps}
                render={(rest) => render({ ...rest, renderProps })}
                search={search}
            />
        );
    }
}

export default withRouter(injectModel("products")(withTranslation()(SearchFilterContainer)));
